import { useEffect, useState } from "react"

export const ACTIVE_THREAD_STATUSES = new Set(["queued", "waiting", "starting", "running"])

export interface ThreadActivity {
    id: string
    status: string
    startedAt?: string
}

export function isActiveThreadStatus(status: string): boolean {
    return ACTIVE_THREAD_STATUSES.has(status.toLowerCase())
}

export function ThreadActivityBar({ activity }: { activity: ThreadActivity }): JSX.Element {
    const [observedAt] = useState(Date.now)
    const [now, setNow] = useState(Date.now)

    useEffect(() => {
        setNow(Date.now())
        const timer = window.setInterval(() => setNow(Date.now()), 1_000)
        return () => window.clearInterval(timer)
    }, [activity.id])

    const parsedStartedAt = activity.startedAt ? Date.parse(activity.startedAt) : Number.NaN
    const startedAt = Number.isFinite(parsedStartedAt) ? parsedStartedAt : observedAt
    const elapsedSeconds = Math.max(0, Math.floor((now - startedAt) / 1_000))
    return (
        <div className="mx-auto w-full max-w-5xl px-3 pb-3 pt-1 sm:px-5" data-thread-activity={activity.id}>
            <div className="flex items-center gap-2 text-[11px] leading-5 text-muted" role="status" aria-label="Working">
                <span className="font-medium text-base-content/70">Working</span>
                <span className="font-mono tabular-nums" aria-hidden="true">
                    {formatElapsed(elapsedSeconds)}
                </span>
            </div>
            <div className="relative h-0.5 overflow-hidden bg-primary/10" aria-hidden="true">
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/80 to-transparent animate-[shimmer_1.25s_ease-in-out_infinite] motion-reduce:animate-none" />
            </div>
        </div>
    )
}

export function formatElapsed(totalSeconds: number): string {
    const seconds = Math.max(0, Math.floor(totalSeconds))
    if (seconds < 60) return `${seconds}s`
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    if (minutes < 60) return `${minutes}m ${String(remainingSeconds).padStart(2, "0")}s`
    const hours = Math.floor(minutes / 60)
    const remainingMinutes = minutes % 60
    return `${hours}h ${String(remainingMinutes).padStart(2, "0")}m ${String(remainingSeconds).padStart(2, "0")}s`
}
